import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SupportInteraction, SupportInteractionInsert, InteractionType } from '@/types/support.types';

/**
 * List interactions for a given session
 */
export function useInteractionsBySession(sessionId: string | null) {
  return useQuery({
    queryKey: ['support-interactions', sessionId],
    queryFn: async (): Promise<SupportInteraction[]> => {
      if (!sessionId) return [];

      const { data, error } = await supabase
        .from('support_interactions')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as unknown as SupportInteraction[];
    },
    enabled: !!sessionId,
  });
}

/**
 * Create a new interaction
 */
export function useCreateInteraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SupportInteractionInsert) => {
      const { data, error } = await supabase
        .from('support_interactions')
        .insert({
          session_id: payload.session_id,
          type: payload.type,
          contact_id: payload.contact_id ?? null,
          message_id: payload.message_id ?? null,
          notes: payload.notes ?? null,
          auto_detected: payload.auto_detected ?? false,
          detection_reason: payload.detection_reason ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as unknown as SupportInteraction;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['support-interactions', variables.session_id] });
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
    },
  });
}

/**
 * Detect interaction type via RPC
 */
export function useDetectInteractionType() {
  return useMutation({
    mutationFn: async ({ sessionId, messageText }: { sessionId: string; messageText: string }): Promise<InteractionType> => {
      const { data, error } = await supabase.rpc('detect_interaction_type', {
        p_session_id: sessionId,
        p_message_text: messageText,
      });

      if (error) throw error;
      return data as InteractionType;
    },
  });
}
